import { useEffect, useState } from 'react';
import { ErrorData, SetStateAction } from '../../../../../types/globals';
import FormLine from '../Basic/FormLine';
import {
  Contract,
  ContractSubscription,
  SubscriptionCheckoutData,
  UnsubscribeSubscriptionAdditionalData,
} from '../../../../../types/user/contract';
import {
  CONFIRM_UNSUBSCRIBE_CHECKED,
  STORAGE_PLUS_PLAN_PACKAGE_AMOUNT,
  SUBSCRIPTION_COMPLETE_TYPE,
  SUBSCRIPTION_STEP,
} from '../../../../../services/constants/user/pages/contract';
import SubmitButton from '../SubmitButton';
import { formatMoney, paramizeObject } from '../../../../../services/helpers/parseData';
import {
  getBilledAmountWithTax,
  getContractExpiredDateByPeriod,
  getSubscriptionCheckoutTypeByStep,
  getSubscriptionPeriodLabel,
  getTaxAmount,
} from '../../../../../services/utils/contract';
import { formatDateTime } from '../../../../../services/helpers/formatTime';
import { PATH_CONTRACT } from '../../../../../services/constants/route/router';
import { makeRequest, request } from '../../../../../services/axios/axios';
import { API } from '../../../../../services/constants/route/api';
import { PAYMENT_METHOD_TYPE } from '../../../../../services/constants/user/payment';
import Spinner from '../../../../commons/icons/Animations/Spinner';
import { swalError, swalMessage } from '../../../../../services/helpers/swal';
import PriceChangeCalculation from './PriceChangeCalculation';
import { RenderConfirmPassWordUnSubscribeSubscription, RenderUnSubscribeSubscriptionAnnouncement } from '../RenderUtils';
import { useNavigate } from 'react-router-dom';
import { useStorage } from '../../../../../hooks/useStorage';
import { STORAGE_KEY_PATH_HISTORY } from '../../../../../services/constants/globals';

type BasicSubscriptionConfirmationProps = {
  setStep?: SetStateAction<string>;
  formData?: ContractSubscription;
  latestPriceList?: Contract[];
  setTitle: SetStateAction<string>;
  originSubscribed?: ContractSubscription;
  step?: string;
};

type RenderContractLabelConfirmType = {
  step: string;
  formData?: ContractSubscription;
  originSubscribed?: ContractSubscription;
  latestPriceList?: Contract[];
};

const RenderContractLabelConfirm = ({
  step,
  formData,
  originSubscribed,
  latestPriceList,
}: RenderContractLabelConfirmType) => {
  const planCapacity =
    latestPriceList && latestPriceList[0] && latestPriceList[0].storage_capacity
      ? latestPriceList[0].storage_capacity
      : STORAGE_PLUS_PLAN_PACKAGE_AMOUNT;
  switch (step) {
    case SUBSCRIPTION_STEP.CONFIRMATION:
      return (
        <>
          （{formatDateTime(new Date())}
          から開始）
        </>
      );
    case SUBSCRIPTION_STEP.CONFIRMATION_CHANGE:
      if (formData?.billed_amount && originSubscribed?.billed_amount) {
        if (formData?.billed_amount <= originSubscribed?.billed_amount) {
          return (
            <>
              （現在のプラン（{getSubscriptionPeriodLabel(originSubscribed?.period_type)}{' '}
              {planCapacity}GBx{originSubscribed?.product_quantity}） は次回の更新日（
              {formatDateTime(
                getContractExpiredDateByPeriod(new Date(), originSubscribed?.period_type),
              )}
              ）まで継続されます。その後、新しいプラン（{planCapacity}GBx{formData?.product_quantity}）と価格で更新されます。）
            </>
          );
        } else {
          return (
            <>
              （現在のプラン（{getSubscriptionPeriodLabel(originSubscribed?.period_type)}{' '}
              {planCapacity}GBx{originSubscribed?.product_quantity}） すぐに新しいプラン（
              {getSubscriptionPeriodLabel(formData?.period_type)} {planCapacity}GBx
              {formData?.product_quantity}）
              に変更されます。元々のサブスクリプションの利用日数に基づく金額が返金されます。)
            </>
          );
        }
      }
      return <></>;
    case SUBSCRIPTION_STEP.CONFIRMATION_UNSUBSCRIBE:
      return (
        <>
          （現在のプラン（{getSubscriptionPeriodLabel(originSubscribed?.period_type)} {planCapacity}
          GBx{originSubscribed?.product_quantity}） は次回の更新日（
          {formatDateTime(
            getContractExpiredDateByPeriod(new Date(), originSubscribed?.period_type),
          )}
          ） まで継続されます。その後、サブスクが解約されます。
        </>
      );

    default:
      return <></>;
  }
};

const StorageSubscriptionConfirmation = ({
  latestPriceList,
  formData,
  setStep,
  setTitle,
  originSubscribed,
  step = '',
}: BasicSubscriptionConfirmationProps) => {
  const navigate = useNavigate();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [verifyData, setVerifyData] = useState<UnsubscribeSubscriptionAdditionalData>({})
  const planCapacity =
    latestPriceList && latestPriceList[0] && latestPriceList[0].storage_capacity
      ? latestPriceList[0].storage_capacity
      : STORAGE_PLUS_PLAN_PACKAGE_AMOUNT;
  const freeCapacity = localStorage.getItem('free-setting-capacity');
  const isCancel = (step === SUBSCRIPTION_STEP.CONFIRMATION_UNSUBSCRIBE) ? true : false;
  const submitText = isCancel ? '契約を解約する' : '決済画面へ進む';
  const { getItem } = useStorage();
  const pathHistory = getItem(STORAGE_KEY_PATH_HISTORY);

  useEffect(() => {
    setTitle('契約状況 ｜契約管理 ｜オプション機能の変更の確認');
  }, []);

  function megabytesToGigabytes(megabytes: number, isFixed = true) {
    if (isFixed) return (megabytes / 1024).toFixed(2);
    return megabytes / 1024;
  }

  const handleChangeAdditionData = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    let additionFormData;
    if (e.target.type === 'checkbox') {
        const target = e.target as HTMLInputElement;
        if (target.checked) {
            additionFormData = {
              ...verifyData,
              confirmation: CONFIRM_UNSUBSCRIBE_CHECKED,
            };
        } else {
            additionFormData = {
              password: verifyData.password
            };
        }
    } else {
        additionFormData = {
          ...verifyData,
          [name]: value
        }
    }
    setVerifyData(additionFormData);
  };

  const submitHandle = async () => {
    let usedStorage = 0;
    let isVerified = true;
    let errorMessage = '';
    setIsCheckingOut(true);

    if(isCancel) {
      await request.post(
        `${API.AUTH.VERIFY_ADMIN}`, 
        verifyData, 
        () => {}, 
        (err: ErrorData) => {
          {Object.keys(err).map((errorKey, index) => (
            errorMessage += err[errorKey][0] + '<br/>'
          ))}
          swalMessage(
            'エラー',
            '',
            'error',
            { timer: 2000, html: errorMessage },
          );
          isVerified = false;
          return;
        }
      )
      if(!isVerified) {
        setIsCheckingOut(false)
        return;
      }
    }

    // get used storage of oz
    await request.get(`${API.STORAGE_PLAN_SUBSCRIPTION.GET_USED_STORAGE}`, (res) => {
      usedStorage = res.used_storage;
    });

    const paramObj = {
      used_storage: megabytesToGigabytes(usedStorage, false),
      product_quantity: formData?.product_quantity,
      plan_capacity: planCapacity,
      free_capacity: freeCapacity,
    };

    // check new storage plan
    const checkStorageResult = await makeRequest({
      method: 'get',
      url: `${API.STORAGE_PLAN_SUBSCRIPTION.CHECK_NEW_PLAN}?${paramizeObject(paramObj)}`,
    });

    if (!checkStorageResult.status) {
      swalMessage(
        'エラー',
        `ストレージ${usedStorage}GBまで利用しましたため、ストレージを減らしてから出ないと変更できません。`,
        'error',
        { timer: 2000 },
      );
      setIsCheckingOut(false);
      return;
    }

    let additionParamString = '';
    switch(step) {
      case SUBSCRIPTION_STEP.CONFIRMATION_CHANGE:
        additionParamString = `?type=${SUBSCRIPTION_COMPLETE_TYPE.CHANGE_COMPLETE}`;
        break;
      case SUBSCRIPTION_STEP.CONFIRMATION_UNSUBSCRIBE:
        additionParamString = `?type=${SUBSCRIPTION_COMPLETE_TYPE.UNSUBSCRIBE_COMPLETE}`;
        break;
      default:
        additionParamString = '';
        break;
    }

    const submitData: SubscriptionCheckoutData = {
      payment_method_type: PAYMENT_METHOD_TYPE.CARD.VALUE,
      success_url: `${window.location.origin}${PATH_CONTRACT.SUBSCRIPTION_COMPLETE}${additionParamString}`,
      cancel_url: window.location.origin + PATH_CONTRACT.OVERVIEW,
      subscription: formData,
      subscription_checkout_type: getSubscriptionCheckoutTypeByStep(step),
    };

    await request.post(
      API.CONTRACT_SUBSCRIPTION.GET_URL_CHECKOUT,
      submitData,
      (res) => {
        window.location.href = res;
        setIsCheckingOut(false);
        return;
      },
      () => {
        setIsCheckingOut(false);
      },
    );
  };

  const returnHandle = () => {
    if(isCancel) {
      if (pathHistory && pathHistory.trim()) {
        return navigate(`${PATH_CONTRACT.OVERVIEW}?${pathHistory.trim()}`);
      }
      
      return navigate(PATH_CONTRACT.OVERVIEW);
    }
    setStep?.(SUBSCRIPTION_STEP.FORM_FILL);
  };

  return (
    <div className="text-secondary-dark">
      <div className="mb-[7px]">契約詳細</div>
      <div className="">
        <FormLine label="ストレージ追加">
          <div className="w-[50%] flex items-center gap-[7px] ml-[16px]">
            ストレージ（{planCapacity}GBの契約）
          </div>
        </FormLine>

        <FormLine label="数量">
          <div className="w-[50%] flex items-center gap-[7px] ml-[16px]">
            {step === SUBSCRIPTION_STEP.CONFIRMATION_UNSUBSCRIBE
              ? originSubscribed?.product_quantity
              : formData?.product_quantity}
          </div>
        </FormLine>

        <FormLine label="契約">
          <div className="ml-[16px] text-[12px] font-[400] leading-[150%]">
            {step === SUBSCRIPTION_STEP.CONFIRMATION_UNSUBSCRIBE
              ? '無し'
              : getSubscriptionPeriodLabel(formData?.period_type)}
            <span className="text-danger text-[12px] font-[400] leading-[100%]">
              <RenderContractLabelConfirm
                step={step || ''}
                formData={formData}
                originSubscribed={originSubscribed}
                latestPriceList={latestPriceList}
              />
            </span>
          </div>
        </FormLine>

        <FormLine label="金額（税抜）">
          <div className="w-full flex items-center ml-[16px] py-[15 px] gap-[20px] pb-[15px]">
            {formatMoney(formData?.billed_amount)}
          </div>
        </FormLine>

        {!isCancel ? (
            <>
              <div className="flex justify-center items-center border-t border-secondary-light mt-[20px] py-[12px]">
                <div className="w-[290px] flex items-center ml-[-70px]">
                  <div className="text-[16px] font-[500] leading-[100%] w-full text-right">小計</div>
                  <div className="text-[18px] font-[500] leading-[100%] w-full text-right">
                    {formatMoney(formData?.billed_amount)}
                  </div>
                </div>
              </div>
              <div className="flex justify-center items-center mt-[20px] py-[5px]">
                <div className="w-[290px] flex items-center ml-[-70px]">
                  <div className="text-[10px] font-[500] leading-[100%] w-full text-right">
                    消費税額（10％対象）
                  </div>
                  <div className="text-[12px] font-[500] leading-[100%] w-full text-right">
                    {formatMoney(getTaxAmount(formData?.billed_amount))}
                  </div>
                </div>
              </div>

              <div className="flex justify-center items-center mt-[20px] py-[12px]">
                <div className="w-[290px] flex items-center ml-[-70px]">
                  <div className="text-primary text-[16px] font-[500] leading-[100%] w-full text-right">
                    料金合計
                  </div>
                  <div className="text-[18px] font-[500] leading-[100%] w-full text-right">
                    {formatMoney(getBilledAmountWithTax(formData?.billed_amount))}
                  </div>
                </div>
              </div>

              {originSubscribed?.id && (
                <div className="w-full flex items-center justify-center py-[25px] gap-[20px]">
                  <PriceChangeCalculation
                    contracts={latestPriceList}
                    subscription={formData}
                    originSubscribed={originSubscribed}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <FormLine label="注意事項">
                <div className="w-full flex items-center ml-[16px] py-[15 px] gap-[20px] pb-[15px]">
                  <RenderUnSubscribeSubscriptionAnnouncement />
                </div>
              </FormLine>
              <div className='2xl:w-[43%] w-[46%] mx-auto pb-[10px] pt-[30px]'>
                <RenderConfirmPassWordUnSubscribeSubscription 
                  handleChange={handleChangeAdditionData}
                />
              </div>
            </>
          )
        }

        <div className="flex justify-center gap-[15px] mt-[30px]">
          <SubmitButton type="return" onClick={returnHandle} disabled={isCheckingOut}>
            前の画面へ戻る
          </SubmitButton>
          <SubmitButton 
            type="unsubscribe" 
            onClick={submitHandle} 
            disabled={isCheckingOut || (isCancel && verifyData.confirmation !== CONFIRM_UNSUBSCRIBE_CHECKED)}
          >
            {isCheckingOut ? <Spinner /> : submitText}
          </SubmitButton>
        </div>
      </div>
    </div>
  );
};

export default StorageSubscriptionConfirmation;
